import { PBEnums } from "~/types/pb/enums";
import type { PBTeamMember } from "~/types/pocketbase";

import { isDebugMode } from "~/composables/log";

export enum RoleRules {
	Params_User,
	Params_Security,
	Params_Subscriptions,

	Params_Stripe_Coord,
	Params_Stripe_PaymentMethod,
	Params_Stripe_Invoice,

	Params_Teams,

	Partner,

	ObjectStorage,
	ObjectStorage_Identifier,
	ObjectStorage_Bucket,
	ObjectStorage_Alerts,
	ObjectStorage_Usage,
	ObjectStorage_Token,

	Drive,

	DrivePro,

	Discover
}

/**
 * Enum representing different types of rules that can be applied.
 *
 * The Rules enum is designed to provide a set of predefined constants
 * that can be used to specify access levels or permissions in various
 * contexts, such as user roles or resource management.
 *
 * Members:
 * - All: Represents full access or all possible operations.
 * - ReadOnly: Represents access limited to reading operations.
 * - Update: Represents access limited to read and update operations.
 * - Delete: Represents access limited to read, update and delete operations.
 * - None: Represents no access or restricted operations.
 */
export enum Rules {
	// noinspection JSUnusedGlobalSymbols
	All,
	ReadOnly,
	Update,
	Delete,
	None
}

/**
 * Represents the authorization configurations for different roles in the system.
 *
 * Each role is mapped to a set of rules defining their permissions for various features
 * and actions within the system. Permissions are categorized and assigned per predefined rules.
 * Certain roles are excluded from the authorizations lookup, specifically the `Owner`
 * and `None` roles.
 *
 * The data structure utilizes `Record` to map roles to their corresponding permissions,
 * and these mappings are further refined using conditional omission of specific roles.
 *
 * Key Points:
 * - Roles come from the `PBEnums.Roles` enumeration.
 * - Rules are defined within the `RoleRules` and `Rules` definitions.
 * - The authorization defines granular control, assigning `Rules.All`, `Rules.None`,
 *   and `Rules.ReadOnly` as necessary per feature.
 */
const roleAuthorizations: Omit<
	Omit<
		Record<PBEnums.Roles, Record<RoleRules, Rules>>,
		PBEnums.Roles.Owner
	>,
	PBEnums.Roles.None
> = {
	[PBEnums.Roles.Admin]: {
		[RoleRules.Params_User]: Rules.All,
		[RoleRules.Params_Security]: Rules.All,
		[RoleRules.Params_Subscriptions]: Rules.All,

		[RoleRules.Params_Stripe_Coord]: Rules.All,
		[RoleRules.Params_Stripe_PaymentMethod]: Rules.All,
		[RoleRules.Params_Stripe_Invoice]: Rules.All,

		[RoleRules.ObjectStorage]: Rules.All,
		[RoleRules.ObjectStorage_Identifier]: Rules.All,
		[RoleRules.ObjectStorage_Bucket]: Rules.All,
		[RoleRules.ObjectStorage_Alerts]: Rules.All,
		[RoleRules.ObjectStorage_Usage]: Rules.All,
		[RoleRules.ObjectStorage_Token]: Rules.All,

		[RoleRules.Partner]: Rules.All,

		[RoleRules.Params_Teams]: Rules.All,

		[RoleRules.Drive]: Rules.All,

		[RoleRules.DrivePro]: Rules.All,

		[RoleRules.Discover]: Rules.All
	},
	[PBEnums.Roles.Invoice]: {
		[RoleRules.Params_User]: Rules.All,
		[RoleRules.Params_Security]: Rules.All,
		[RoleRules.Params_Subscriptions]: Rules.All,

		[RoleRules.Params_Stripe_Coord]: Rules.All,
		[RoleRules.Params_Stripe_PaymentMethod]: Rules.All,
		[RoleRules.Params_Stripe_Invoice]: Rules.All,

		[RoleRules.ObjectStorage]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Identifier]: Rules.None,
		[RoleRules.ObjectStorage_Bucket]: Rules.None,
		[RoleRules.ObjectStorage_Alerts]: Rules.None,
		[RoleRules.ObjectStorage_Usage]: Rules.None,
		[RoleRules.ObjectStorage_Token]: Rules.None,

		[RoleRules.Partner]: Rules.ReadOnly,

		[RoleRules.Params_Teams]: Rules.None,

		[RoleRules.Drive]: Rules.None,

		[RoleRules.DrivePro]: Rules.None,

		[RoleRules.Discover]: Rules.None
	},
	[PBEnums.Roles.Stats]: {
		[RoleRules.Params_User]: Rules.All,
		[RoleRules.Params_Security]: Rules.All,
		[RoleRules.Params_Subscriptions]: Rules.None,

		[RoleRules.Params_Stripe_Coord]: Rules.None,
		[RoleRules.Params_Stripe_PaymentMethod]: Rules.None,
		[RoleRules.Params_Stripe_Invoice]: Rules.None,

		[RoleRules.ObjectStorage]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Identifier]: Rules.None,
		[RoleRules.ObjectStorage_Bucket]: Rules.None,
		[RoleRules.ObjectStorage_Alerts]: Rules.None,
		[RoleRules.ObjectStorage_Usage]: Rules.All,
		[RoleRules.ObjectStorage_Token]: Rules.None,

		[RoleRules.Partner]: Rules.ReadOnly,

		[RoleRules.Params_Teams]: Rules.None,

		[RoleRules.Drive]: Rules.None,

		[RoleRules.DrivePro]: Rules.None,

		[RoleRules.Discover]: Rules.None
	},
	[PBEnums.Roles.S3ReadOnly]: {
		[RoleRules.Params_User]: Rules.All,
		[RoleRules.Params_Security]: Rules.All,
		[RoleRules.Params_Subscriptions]: Rules.None,

		[RoleRules.Params_Stripe_Coord]: Rules.None,
		[RoleRules.Params_Stripe_PaymentMethod]: Rules.None,
		[RoleRules.Params_Stripe_Invoice]: Rules.None,

		[RoleRules.ObjectStorage]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Identifier]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Bucket]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Alerts]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Usage]: Rules.ReadOnly,
		[RoleRules.ObjectStorage_Token]: Rules.ReadOnly,

		[RoleRules.Partner]: Rules.ReadOnly,

		[RoleRules.Params_Teams]: Rules.None,

		[RoleRules.Drive]: Rules.None,

		[RoleRules.DrivePro]: Rules.None,

		[RoleRules.Discover]: Rules.None
	}
};

/**
 * Determines the authorization rules for a given role and rule.
 *
 * @param {PBEnums.Roles} role - The role for which authorization is being determined.
 *                              This is expected to be one of the predefined roles in PBEnums.Roles.
 * @param {RoleRules} rule - The specific rule to evaluate against the provided role.
 * @return {Rules} The corresponding set of rules (permissions or restrictions) associated with
 *                 the provided role and rule. Defaults to `Rules.None` if no specific authorization is found.
 */
export function getRoleAuthorization (role: PBEnums.Roles, rule: RoleRules): Rules {
	if (role === PBEnums.Roles.Owner) {
		return Rules.All;
	}

	if (role === PBEnums.Roles.None) {
		return Rules.None;
	}

	if (role in roleAuthorizations && rule in roleAuthorizations[role]) {
		return roleAuthorizations[role][rule];
	}

	return Rules.None;
}

/**
 * Retrieves the authorization rules based on the provided role rules.
 *
 * @param {RoleRules} rule - The set of rules associated with a specific user role.
 * @return {Rules} The calculated authorization rules for the specified role.
 */
export function getAuthorization (rule: RoleRules): Rules {
	// Support des drives legacy qui ne sont pas dans PB.
	if (rule === RoleRules.Drive) return Rules.All;

	const role = __pbUser().getRole;
	return getRoleAuthorization(role, rule);
}

/**
 * Determines whether a user has authorization based on the provided role rules.
 *
 * @param {RoleRules} rule - The role rules to evaluate for determining authorization.
 * @return {boolean} Returns true if the user has authorization, otherwise false.
 */
export function hasAuthorization (rule: RoleRules): boolean {
	if (isDebugMode()) return true;

	const authorization = getAuthorization(rule);

	return authorization !== Rules.None;
}

/**
 * Determines if the provided authorization rule corresponds to a read-only access level.
 *
 * @param {RoleRules} rule - The role rules used to determine the authorization level.
 * @return {boolean} Returns true if the authorization level is read-only, otherwise false.
 */
export function isReadOnlyAuthorization (rule: RoleRules): boolean {
	const authorization = getAuthorization(rule);

	return authorization === Rules.ReadOnly;
}

/**
 * Sorts members based on their role priority and creation date. Members with higher priority roles
 * are sorted first, and if roles are identical, they are sorted by their creation timestamp.
 *
 * @param {PBTeamMember} memberA The first team member to be compared.
 * @param {PBTeamMember} memberB The second team member to be compared.
 * @return {number} A negative number if memberA should come before memberB, a positive number if
 * memberB should come before memberA, or 0 if they are considered equal.
 */
export function sortMembers (memberA: PBTeamMember, memberB: PBTeamMember): number {
	const rolePriority: Record<PBEnums.Roles, number> = {
		[PBEnums.Roles.Owner]: 0,
		[PBEnums.Roles.Admin]: 1,
		[PBEnums.Roles.Invoice]: 2,
		[PBEnums.Roles.Stats]: 3,
		[PBEnums.Roles.S3ReadOnly]: 4,
		[PBEnums.Roles.None]: 999
	};

	const roleComparison = rolePriority[memberA.role] - rolePriority[memberB.role];
	if (roleComparison !== 0) {
		return roleComparison;
	}

	// If roles are identical, sort by creation date
	const dateA = new Date(memberA.created);
	const dateB = new Date(memberB.created);
	return dateA.getTime() - dateB.getTime();
}
